import { CurrencyCodes, type SecurityTypes } from '@onevesthq/ov-enums';
import type { TranslatedString } from './shared';

export enum FinancialProductState {
  DRAFT = 'DRAFT',
  ACTIVE = 'ACTIVE',
  ARCHIVED = 'ARCHIVED',
}

export enum Strategy {
  STANDARD = 'STANDARD',
  ASSET_CLASS = 'ASSET_CLASS',
}

export enum AssetClassLevel {
  PRIMARY = 'PRIMARY',
  SECONDARY = 'SECONDARY',
  TERTIARY = 'TERTIARY',
}

/** Maps each AssetClassLevel to the corresponding FinancialProduct property key. */
export const ASSET_CLASS_FIELD_BY_LEVEL: Record<
  AssetClassLevel,
  'primaryAssetClass' | 'secondaryAssetClass' | 'tertiaryAssetClass'
> = {
  [AssetClassLevel.PRIMARY]: 'primaryAssetClass',
  [AssetClassLevel.SECONDARY]: 'secondaryAssetClass',
  [AssetClassLevel.TERTIARY]: 'tertiaryAssetClass',
};

export enum PortfolioTemplateState {
  DRAFT = 'DRAFT',
  ACTIVE = 'ACTIVE',
  ARCHIVED = 'ARCHIVED',
}

export enum Exchanges {
  TSX = 'TSX',
  NASDAQ = 'NASDAQ',
  NYSE = 'NYSE',
  FUNDSERV = 'FUNDSERV',
  PTF = 'PTF',
  PRIVATE_SECURITY = 'PRIVATE_SECURITY',
  CANNEX = 'CANNEX',
  CBOE = 'CBOE',
  CBOE_CAD = 'CBOE_CAD',
  CME = 'CME',
  CHX = 'CHX',
  ISE = 'ISE',
  PHLX = 'PHLX',
  MS4X = 'MS4X',
  TSX_V = 'TSX_V',
}

export const EXCHANGE_COUNTRIES = {
  US: 'US',
  CA: 'CA',
};

export const EXCHANGES_COUNTRIES_MAP = {
  [Exchanges.NASDAQ.toString()]: {
    country: EXCHANGE_COUNTRIES.US,
    currency: CurrencyCodes.USD,
  },
  [Exchanges.NYSE.toString()]: {
    country: EXCHANGE_COUNTRIES.US,
    currency: CurrencyCodes.USD,
  },
  [Exchanges.CBOE.toString()]: {
    country: EXCHANGE_COUNTRIES.US,
    currency: CurrencyCodes.USD,
  },
  [Exchanges.CBOE_CAD.toString()]: {
    country: EXCHANGE_COUNTRIES.CA,
    currency: CurrencyCodes.CAD,
  },
  [Exchanges.CME.toString()]: {
    country: EXCHANGE_COUNTRIES.US,
    currency: CurrencyCodes.USD,
  },
  [Exchanges.CHX.toString()]: {
    country: EXCHANGE_COUNTRIES.US,
    currency: CurrencyCodes.USD,
  },
  [Exchanges.ISE.toString()]: {
    country: EXCHANGE_COUNTRIES.US,
    currency: CurrencyCodes.USD,
  },
  [Exchanges.PHLX.toString()]: {
    country: EXCHANGE_COUNTRIES.US,
    currency: CurrencyCodes.USD,
  },
  [Exchanges.MS4X.toString()]: {
    country: EXCHANGE_COUNTRIES.US,
    currency: CurrencyCodes.USD,
  },
  [Exchanges.TSX.toString()]: {
    country: EXCHANGE_COUNTRIES.CA,
    currency: CurrencyCodes.CAD,
  },
  [Exchanges.TSX_V.toString()]: {
    country: EXCHANGE_COUNTRIES.CA,
    currency: CurrencyCodes.CAD,
  },
  [Exchanges.FUNDSERV.toString()]: {
    country: EXCHANGE_COUNTRIES.CA,
    currency: CurrencyCodes.CAD,
  },
  [Exchanges.CANNEX.toString()]: {
    country: EXCHANGE_COUNTRIES.CA,
    currency: CurrencyCodes.CAD,
  },
  [Exchanges.PTF.toString()]: {
    country: EXCHANGE_COUNTRIES.CA,
    currency: CurrencyCodes.CAD,
  },
  [Exchanges.PRIVATE_SECURITY.toString()]: {
    country: EXCHANGE_COUNTRIES.CA,
    currency: CurrencyCodes.CAD,
  },
};

export enum ThemeState {
  DRAFT = 'DRAFT',
  ACTIVE = 'ACTIVE',
  ARCHIVED = 'ARCHIVED',
}

export type Theme = {
  id?: string;
  name?: string;
  description?: string;
  translatedName?: TranslatedString;
  translatedDescription?: TranslatedString;
  translatedShortDescription?: TranslatedString;
  key?: string;
  core?: boolean;
  state?: ThemeState;
  iconKey?: string;
};

export type AssetClass = {
  id?: string | null;
  key?: string;
  name?: string;
  description?: string;
  translatedName?: TranslatedString;
  translatedDescription?: TranslatedString;
  state?: string;
  riskLevel?: number;
};

export type Branch = {
  id?: string;
  percentage: number;
  financialProduct: FinancialProduct;
  taxRank?: number;
  isCashEquivalent?: boolean;
  assetClass?: AssetClass;
};

export type AssetClassBranch = {
  id: string;
  targetPercentage: number | null;
  assetClass: AssetClass;
  color?:
    | 'supportOne'
    | 'supportTwo'
    | 'supportThree'
    | 'supportFour'
    | 'supportFive'
    | 'supportSix';
};

export type PortfolioTemplate = {
  id: string;
  name: string;
  state: PortfolioTemplateState;
  translatedName: TranslatedString | undefined;
  translatedTemplateDescription: TranslatedString | undefined;
  assetClassBranches: AssetClassBranch[] | undefined;
  createdAt: string;
  updatedAt: string;
};

export type TargetAllocation = {
  /** Legacy name — holds whichever asset class level (primary or secondary) the
   *  user selected via the "Group By" toggle, not strictly the secondary level. */
  secondaryAssetClass: AssetClass;
  min: number;
  max: number;
};

export type Schedule = {
  dates?: string[];
  nextDate?: string;
  id?: string;
  name?: string;
  associatedFinancialProductsCount?: number;
};

export type FinancialProduct = {
  id?: string;
  name?: string;
  url?: string;
  translatedUrl?: TranslatedString;
  translatedName?: TranslatedString;
  translatedPortfolioDisclaimer?: TranslatedString;
  state?: FinancialProductState;
  suggestedMinScore?: number;
  suggestedMaxScore?: number;
  lowThresholdScore?: number;
  tradeable?: boolean;
  suggestable?: boolean;
  autoUpdatePrices?: boolean;
  assignable?: boolean;
  cusip?: string | null;
  ticker?: string | null;
  exchange?: Exchanges;
  ric?: string;
  forecastedRateOfReturn?: number;
  forecastedStandardDeviation?: number;
  currentPriceCents?: number;
  originalCurrentPriceCents?: number;
  currencyCurrentPriceCents?: {
    CAD?: number;
    USD?: number;
  };
  children?: Branch[];
  theme?: Theme;
  primaryAssetClass?: AssetClass;
  secondaryAssetClass?: AssetClass;
  tertiaryAssetClass?: AssetClass;
  portfolioDescription?: string;
  translatedPortfolioDescription?: TranslatedString;
  investmentObjective?: string;
  translatedInvestmentObjective?: TranslatedString;
  translatedLiquidity?: TranslatedString;
  targetAllocations?: TargetAllocation[];
  updatedAt?: Date;
  riskReturnLevel?: number;
  reasonForUpdate?: string;
  translatedReasonForUpdate?: TranslatedString;
  isCash?: boolean;
  substituteFinancialProduct?: FinancialProduct;
  isPartial?: boolean;
  taxRank?: number | null;
  settlementDays?: number | null;
  currency?: CurrencyCodes;
  originalCurrency?: CurrencyCodes;
  translatedNickName?: TranslatedString;
  riskProfileName?: TranslatedString;
  portfolioTemplate?: PortfolioTemplate;
  zeroPercentCash?: boolean;
  strategy?: Strategy;
  assetClassLevel?: AssetClassLevel;
  assetClassBranches?: AssetClassBranch[];
  type?: SecurityTypes;
  schedule?: Schedule;
  isin?: string;
  volatile?: boolean;
  waivedFeePercentage?: number | null;
  multiplier?: number | null;
  fundSeries?: string | null;
  /**
   * Nullable on the SDL: `null` means the usage-count upstream was unavailable.
   * Callers MUST treat `null` as "unknown" (e.g. disable destructive actions),
   * never as 0.
   */
  assignedCount?: number | null;
};
